import React, { useState, useCallback, useRef, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useLesson, useMarkLessonComplete, useLessonContentUrl } from '@/hooks'
import { useAuth } from '@/hooks/useAuth'
import { useEnrollments } from '@/hooks/useEnrollments'
import { VideoPlayer, PDFViewer, TextContent } from '@/components/lessons'
import type { LessonContentType } from '@/types/lesson'

interface LessonViewerPageProps {
  enrollmentId?: string
}

export const LessonViewerPage: React.FC<LessonViewerPageProps> = ({ enrollmentId: propEnrollmentId }) => {
  const { courseId, lessonId, enrollmentId: paramEnrollmentId } = useParams<{
    courseId: string
    lessonId: string
    enrollmentId: string
  }>()
  const navigate = useNavigate()
  const { user } = useAuth()

  // Fetch enrollments to find the enrollment for this course
  const { data: enrollments } = useEnrollments({ userId: user?.id })

  // Find the enrollment for the current course
  const foundEnrollment = useMemo(() => {
    if (!enrollments || !courseId) return null
    return enrollments.find((e: { courseId: string; status: string }) =>
      e.courseId === courseId && e.status === 'active'
    )
  }, [enrollments, courseId])

  // Determine enrollmentId for marking completion
  const enrollmentId = propEnrollmentId || paramEnrollmentId || foundEnrollment?.id

  // Check if this is preview mode or enrolled mode
  // Preview mode: URL is /courses/:courseId/lessons/:lessonId (no enrollmentId in params)
  // Enrolled mode: accessed from ClassroomPage /learn/:enrollmentId
  const isPreviewMode = !paramEnrollmentId

  const { data: lesson, isLoading, error } = useLesson(lessonId || '')
  const { data: contentData, error: contentError } = useLessonContentUrl(
    lessonId || '',
    !!lesson && lesson.contentType !== 'text' // Only fetch for non-text content
  )
  const markCompleteMutation = useMarkLessonComplete()

  const watchTimeRef = useRef(0)
  const [isCompleted, setIsCompleted] = useState(false)

  const handleTimeUpdate = useCallback((currentTime: number) => {
    watchTimeRef.current = currentTime
  }, [])

  const handleVideoEnded = useCallback(() => {
    if (lessonId && enrollmentId && !isCompleted) {
      markCompleteMutation.mutate(
        { lessonId, enrollmentId },
        {
          onSuccess: () => {
            setIsCompleted(true)
          },
        }
      )
    }
  }, [lessonId, enrollmentId, isCompleted, markCompleteMutation])

  const handleBack = useCallback(() => {
    // If in preview mode, always go back to course detail
    if (isPreviewMode && courseId) {
      navigate(`/course/${courseId}`)
    }
    // If in enrolled mode, go back to classroom
    else if (paramEnrollmentId) {
      navigate(`/learn/${paramEnrollmentId}`)
    }
    // Fallback
    else if (courseId) {
      navigate(`/course/${courseId}`)
    } else {
      navigate(-1)
    }
  }, [isPreviewMode, paramEnrollmentId, courseId, navigate])

  const renderContent = (contentType: LessonContentType, contentUrl: string, secureUrl?: string) => {
    // For text content, use the contentUrl directly (it's the actual text)
    // For video/pdf, use the secure presigned URL
    const mediaUrl = contentType === 'text' ? contentUrl : secureUrl

    if (contentType !== 'text' && !mediaUrl) {
      return (
        <div className="flex flex-col items-center justify-center py-10 text-text-muted">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4" />
          <p>Loading content...</p>
        </div>
      )
    }

    switch (contentType) {
      case 'video':
        return (
          <VideoPlayer
            src={mediaUrl!}
            onTimeUpdate={handleTimeUpdate}
            onEnded={handleVideoEnded}
          />
        )
      case 'pdf':
        return <PDFViewer src={mediaUrl!} />
      case 'text':
        // Use HTML content if available (rich text), otherwise fall back to plain text
        const textContent = lesson?.contentHtml || contentUrl
        return <TextContent content={textContent} />
      default:
        return (
          <div className="text-center text-text-muted py-10">
            Unsupported content type
          </div>
        )
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background-dark flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-text-muted">Loading lesson...</p>
        </div>
      </div>
    )
  }

  if (error || !lesson) {
    return (
      <div className="min-h-screen bg-background-dark flex flex-col items-center justify-center p-6">
        <span className="material-symbols-outlined text-6xl text-gray-400 mb-4">error</span>
        <p className="text-lg font-bold text-white mb-2">Lesson not found</p>
        <p className="text-text-muted mb-6">
          {error instanceof Error ? error.message : 'The lesson you are looking for does not exist.'}
        </p>
        <button
          onClick={handleBack}
          className="px-6 py-2.5 bg-primary text-black rounded-lg font-bold hover:bg-primary/90 transition-colors"
        >
          Go Back
        </button>
      </div>
    )
  }

  if (contentError) {
    return (
      <div className="min-h-screen bg-background-dark flex flex-col items-center justify-center p-6">
        <span className="material-symbols-outlined text-6xl text-gray-400 mb-4">lock</span>
        <p className="text-lg font-bold text-white mb-2">Access Denied</p>
        <p className="text-text-muted mb-6">
          {contentError instanceof Error ? contentError.message : 'You do not have access to this content.'}
        </p>
        <button
          onClick={handleBack}
          className="px-6 py-2.5 bg-primary text-black rounded-lg font-bold hover:bg-primary/90 transition-colors"
        >
          Go Back
        </button>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-slate-950 selection:bg-primary/30">
      {/* Main Content Area */}
      <main className="max-w-5xl mx-auto px-4 py-8 pt-24 md:pt-32">
        
        {/* Navigation & Header Area */}
        <div className="mb-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-5">
            <button
              onClick={handleBack}
              className="group flex items-center gap-2.5 px-4 py-2 rounded-2xl bg-white/5 hover:bg-primary/20 text-white transition-all duration-300 border border-white/10 hover:border-primary/30 shadow-2xl backdrop-blur-sm shrink-0"
              aria-label="Go back"
            >
              <span className="material-symbols-outlined text-[18px] group-hover:-translate-x-1 transition-transform duration-300">arrow_back</span>
              <span className="text-xs font-bold tracking-tight">Kembali</span>
            </button>

            <div className="h-8 w-px bg-white/10 hidden md:block" />

            <div className="flex flex-col gap-1.5">
              <div className="flex flex-wrap items-center gap-2.5">
                <span className={`inline-flex items-center px-2 py-0.5 rounded-lg text-[8px] font-black uppercase tracking-widest ${
                  lesson.contentType === 'video' ? 'bg-rose-500/20 text-rose-400' : 
                  lesson.contentType === 'pdf' ? 'bg-amber-500/20 text-amber-400' : 'bg-blue-500/20 text-blue-400'
                } border border-white/5`}>
                  {lesson.contentType}
                </span>
                <h1 className="text-xl md:text-2xl font-black text-white tracking-tight leading-none">
                  {lesson.title}
                </h1>
              </div>
              {lesson.durationMinutes > 0 && (
                <div className="flex items-center gap-1.5 ml-0.5 opacity-60">
                  <span className="material-symbols-outlined text-[12px] text-slate-400">schedule</span>
                  <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">
                    {lesson.durationMinutes} Menit Sesi
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Completion status indicator (read-only) */}
          {enrollmentId && isCompleted && (
            <div className="flex items-center gap-2.5 px-6 py-3 rounded-2xl bg-green-500/10 text-green-400 border border-green-500/20 font-bold text-sm shrink-0">
              <span className="material-symbols-outlined text-[18px]">check_circle</span>
              Sudah Selesai
            </div>
          )}
        </div>

        {/* Content Viewer (Video/PDF) */}
        <div className="mb-10 rounded-[2rem] overflow-hidden shadow-2xl border border-white/5 bg-black/40 backdrop-blur-sm">
          {renderContent(lesson.contentType, lesson.contentUrl, contentData?.url)}
        </div>

        {/* Info Grid */}
        <div className="space-y-8">
          {/* Description */}
          <div className="w-full space-y-4">
            <div className="flex items-center gap-3 ml-2">
              <div className="size-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                <span className="material-symbols-outlined text-[18px]">info</span>
              </div>
              <h2 className="text-base font-bold text-white tracking-tight">Tentang Materi</h2>
            </div>
            <div className="p-8 rounded-[2rem] bg-white/5 border border-white/5">
              <p className="text-slate-300 leading-relaxed text-sm font-medium whitespace-pre-wrap">
                {lesson.description || 'Tidak ada deskripsi untuk materi ini.'}
              </p>
            </div>
          </div>

          {/* Resources */}
          {lesson.resources && lesson.resources.trim() !== '' && lesson.resources !== '[]' && (
            <div className="w-full space-y-4">
              <div className="flex items-center gap-3 ml-2">
                <div className="size-8 rounded-lg bg-amber-500/10 text-amber-500 flex items-center justify-center">
                  <span className="material-symbols-outlined text-[18px]">attachment</span>
                </div>
                <h2 className="text-base font-bold text-white tracking-tight">Resource Tambahan</h2>
              </div>
              <div className="p-8 rounded-[2rem] bg-amber-500/5 border border-amber-500/10">
                <TextContent content={lesson.resources} />
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  )
}

export default LessonViewerPage
